import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner, faSearch, faGift, faPlus, faCheck, faClock,
  faBan, faHistory, faUser, faEnvelope, faCog, faPen, faTrash,
  faXmark, faArrowUp, faArrowDown, faStar, faMinus,
} from "@fortawesome/free-solid-svg-icons";
import {
  getSpinUsers, grantSpin, revokeSpin, getSpinHistory, updateSpinStatus,
  getWheelTiers, createWheelTier, updateWheelTier, deleteWheelTier,
  createTierPrize, updateTierPrize, deleteTierPrize, setUserTier,
  getSurveyResponses, getSurveySetting, toggleSpinSurvey,
} from "../api/admin";
import "./SpinManagement.css";

const STATUS_MAP = {
  pending:   { label: "Pendente",  cls: "pending",   icon: faClock },
  delivered: { label: "Entregue",  cls: "delivered",  icon: faCheck },
  expired:   { label: "Expirado",  cls: "expired",    icon: faBan },
};

const EMPTY_PRIZE = { prize_key: "", label: "", emoji: "🎁", weight: 10, is_active: true };

const SpinManagement = () => {
  const [tab, setTab] = useState("users");

  // Users tab
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  // History tab
  const [history, setHistory] = useState([]);
  const [historyFilter, setHistoryFilter] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);

  // Grant modal
  const [grantModal, setGrantModal] = useState(null);
  const [grantSpins, setGrantSpins] = useState(1);
  const [grantNote, setGrantNote] = useState("");
  const [grantTierId, setGrantTierId] = useState("");
  const [grantPrizeKey, setGrantPrizeKey] = useState("");
  const [granting, setGranting] = useState(false);

  const [revokeModal, setRevokeModal] = useState(null);
  const [revokeSpins, setRevokeSpins] = useState(1);
  const [revokeNote, setRevokeNote] = useState("");
  const [revoking, setRevoking] = useState(false);

  // Tiers tab
  const [tiers, setTiers] = useState([]);
  const [tiersLoading, setTiersLoading] = useState(false);
  const [tierModal, setTierModal] = useState(null); // null | "new" | tier object
  const [tierForm, setTierForm] = useState({ name: "", emoji: "⭐", color: "#D4AF37", sort_order: 0, is_default: false });
  const [expandedTier, setExpandedTier] = useState(null);
  const [prizeModal, setPrizeModal] = useState(null); // { tierId, prize? }
  const [prizeForm, setPrizeForm] = useState({ ...EMPTY_PRIZE });

  // Survey tab
  const [survey, setSurvey] = useState([]);
  const [surveyTotal, setSurveyTotal] = useState(0);
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [surveyEnabled, setSurveyEnabled] = useState(false);
  const [surveyPage, setSurveyPage] = useState(0);
  const [togglingsurvey, setTogglingSurvey] = useState(false);

  // Tier change for user
  const [tierChangeUser, setTierChangeUser] = useState(null);
  const [selectedTierId, setSelectedTierId] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSpinUsers(page * 20, 20, searchDebounced);
      setUsers(res.users || []);
      setUsersTotal(res.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, searchDebounced]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await getSpinHistory(historyFilter);
      setHistory(res.results || []);
    } catch (e) { console.error(e); }
    finally { setHistoryLoading(false); }
  }, [historyFilter]);

  const loadTiers = useCallback(async () => {
    setTiersLoading(true);
    try {
      const res = await getWheelTiers();
      setTiers(res.tiers || []);
    } catch (e) { console.error(e); }
    finally { setTiersLoading(false); }
  }, []);

  const loadSurvey = useCallback(async () => {
    setSurveyLoading(true);
    try {
      const [resData, resSetting] = await Promise.all([
        getSurveyResponses(surveyPage * 50, 50),
        getSurveySetting(),
      ]);
      setSurvey(resData.responses || []);
      setSurveyTotal(resData.total || 0);
      setSurveyEnabled(resSetting.enabled || false);
    } catch (e) { console.error(e); }
    finally { setSurveyLoading(false); }
  }, [surveyPage]);

  useEffect(() => { if (tab === "users") loadUsers(); }, [tab, loadUsers]);
  useEffect(() => { if (tab === "history") loadHistory(); }, [tab, loadHistory]);
  useEffect(() => { if (tab === "tiers") loadTiers(); }, [tab, loadTiers]);
  useEffect(() => { if (tab === "survey") loadSurvey(); }, [tab, loadSurvey]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadTiers(); }, []);

  const handleGrant = async () => {
    if (!grantModal || granting) return;
    setGranting(true);
    try {
      await grantSpin(grantModal.id, grantSpins, grantNote, grantTierId || null, grantPrizeKey || null);
      
      // Se selecionou um tier diferente do atual da usuária, atualiza o tier dela permanentemente
      if (grantTierId && grantTierId !== grantModal.wheel_tier_id) {
        await setUserTier(grantModal.id, grantTierId);
      }

      setGrantModal(null); setGrantSpins(1); setGrantNote(""); setGrantTierId(""); setGrantPrizeKey("");
      // Pequeno delay para garantir que o banco atualizou o tier antes de recarregar a lista
      setTimeout(() => loadUsers(), 300);
    } catch (e) { alert(e?.response?.data?.detail || "Erro ao conceder giro"); }
    finally { setGranting(false); }
  };

  const handleRevoke = async () => {
    if (!revokeModal || revoking) return;
    const max = Math.max(0, revokeModal.spins_available || 0);
    const qty = Math.min(Math.max(1, revokeSpins), max);
    if (max < 1) {
      alert("Esta usuária não tem giros disponíveis para remover.");
      return;
    }
    if (!window.confirm(`Remover ${qty} giro${qty > 1 ? "s" : ""} de ${revokeModal.name || revokeModal.email}?`)) {
      return;
    }
    setRevoking(true);
    try {
      await revokeSpin(revokeModal.id, qty, revokeNote);
      setRevokeModal(null);
      setRevokeSpins(1);
      setRevokeNote("");
      setTimeout(() => loadUsers(), 300);
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro ao remover giro");
    } finally {
      setRevoking(false);
    }
  };

  const handleStatusChange = async (resultId, newStatus) => {
    try { await updateSpinStatus(resultId, newStatus); loadHistory(); }
    catch (e) { alert(e?.response?.data?.detail || "Erro"); }
  };

  const handleSaveTier = async () => {
    try {
      if (tierModal && tierModal.id) {
        await updateWheelTier(tierModal.id, tierForm);
      } else {
        await createWheelTier(tierForm);
      }
      setTierModal(null);
      loadTiers();
    } catch (e) { alert(e?.response?.data?.detail || "Erro ao salvar tier"); }
  };

  const handleDeleteTier = async (id) => {
    if (!window.confirm("Remover este tipo de roleta? Usuárias nele voltarão ao padrão.")) return;
    try { await deleteWheelTier(id); loadTiers(); }
    catch (e) { alert(e?.response?.data?.detail || "Erro ao remover"); }
  };

  const handleSavePrize = async () => {
    if (!prizeModal) return;
    try {
      if (prizeModal.prize) {
        await updateTierPrize(prizeModal.prize.id, prizeForm);
      } else {
        await createTierPrize(prizeModal.tierId, prizeForm);
      }
      setPrizeModal(null);
      loadTiers();
    } catch (e) { alert(e?.response?.data?.detail || "Erro ao salvar prêmio"); }
  };

  const handleDeletePrize = async (prizeId) => {
    if (!window.confirm("Remover este prêmio?")) return;
    try { await deleteTierPrize(prizeId); loadTiers(); }
    catch (e) { alert(e?.response?.data?.detail || "Erro"); }
  };

  const handleSetUserTier = async () => {
    if (!tierChangeUser) return;
    try {
      await setUserTier(tierChangeUser.id, selectedTierId || null);
      setTierChangeUser(null);
      loadUsers();
    } catch (e) { alert(e?.response?.data?.detail || "Erro"); }
  };

  const handleToggleSurvey = async () => {
    setTogglingSurvey(true);
    try {
      const res = await toggleSpinSurvey(!surveyEnabled);
      setSurveyEnabled(res.enabled);
    } catch (e) { alert(e?.response?.data?.detail || "Erro"); }
    finally { setTogglingSurvey(false); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const getTierName = (tierId) => {
    const t = tiers.find((t) => t.id === tierId);
    return t ? `${t.emoji} ${t.name}` : "Padrão";
  };

  // ── Users tab ──
  const renderUsers = () => (
    <>
      <div className="spin-search-bar">
        <FontAwesomeIcon icon={faSearch} className="spin-search-icon" />
        <input className="spin-search-input" placeholder="Buscar por nome ou email..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} />
      </div>
      {loading ? (
        <div className="spin-loading"><FontAwesomeIcon icon={faSpinner} spin /> Carregando...</div>
      ) : users.length === 0 ? (
        <div className="spin-empty"><FontAwesomeIcon icon={faUser} style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }} /><div>Nenhuma usuária encontrada.</div></div>
      ) : (
        <>
          <div className="spin-users-list">
            {users.map((u) => (
              <div key={u.id} className="spin-user-card">
                <div className="spin-user-avatar">{(u.name || "?").charAt(0).toUpperCase()}</div>
                <div className="spin-user-info">
                  <div className="spin-user-name">{u.name || "Sem nome"}</div>
                  <div className="spin-user-email"><FontAwesomeIcon icon={faEnvelope} /> {u.email}</div>
                </div>
                <div className="spin-user-stats">
                  <div className="spin-stat"><span className="spin-stat-value">{u.spins_available}</span><span className="spin-stat-label">Disponíveis</span></div>
                  <div className="spin-stat"><span className="spin-stat-value">{u.total_prizes}</span><span className="spin-stat-label">Prêmios</span></div>
                  <div className="spin-stat"><span className="spin-stat-value spin-stat-pending">{u.pending_prizes}</span><span className="spin-stat-label">Pendentes</span></div>
                </div>
                <div className="spin-user-actions-row">
                  <button className="spin-grant-btn" onClick={() => setGrantModal(u)}><FontAwesomeIcon icon={faPlus} /> Dar Giro</button>
                  <button
                    type="button"
                    className="spin-revoke-btn"
                    disabled={(u.spins_available || 0) < 1}
                    onClick={() => {
                      setRevokeModal(u);
                      setRevokeSpins(Math.min(1, u.spins_available || 1));
                      setRevokeNote("");
                    }}
                  >
                    <FontAwesomeIcon icon={faMinus} /> Remover Giro
                  </button>
                  <button className="spin-tier-btn" onClick={() => { setTierChangeUser(u); setSelectedTierId(u.wheel_tier_id || ""); }}>
                    <FontAwesomeIcon icon={faStar} /> {getTierName(u.wheel_tier_id)}
                  </button>
                </div>
                {u.prizes && u.prizes.length > 0 && (
                  <div className="spin-user-prizes">
                    {u.prizes.map((p) => {
                      const sc = STATUS_MAP[p.status] || STATUS_MAP.pending;
                      return (
                        <div key={p.id} className="spin-mini-prize">
                          <span>{p.prize_emoji}</span>
                          <span className="spin-mini-label">{p.prize_label}</span>
                          <span className={`spin-mini-status ${sc.cls}`}><FontAwesomeIcon icon={sc.icon} /> {sc.label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="spin-pagination">
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Anterior</button>
            <span>Página {page + 1} de {Math.max(1, Math.ceil(usersTotal / 20))}</span>
            <button disabled={(page + 1) * 20 >= usersTotal} onClick={() => setPage((p) => p + 1)}>Próxima</button>
          </div>
        </>
      )}
    </>
  );

  // ── History tab ──
  const renderHistory = () => (
    <>
      <div className="spin-history-filters">
        {[{ val: "", label: "Todos" }, { val: "pending", label: "Pendentes" }, { val: "delivered", label: "Entregues" }, { val: "expired", label: "Expirados" }].map((f) => (
          <button key={f.val} className={`spin-filter-btn ${historyFilter === f.val ? "active" : ""}`} onClick={() => setHistoryFilter(f.val)}>{f.label}</button>
        ))}
      </div>
      {historyLoading ? (
        <div className="spin-loading"><FontAwesomeIcon icon={faSpinner} spin /> Carregando...</div>
      ) : history.length === 0 ? (
        <div className="spin-empty"><FontAwesomeIcon icon={faHistory} style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }} /><div>Nenhum resultado encontrado.</div></div>
      ) : (
        <div className="spin-history-list">
          {history.map((r) => {
            const sc = STATUS_MAP[r.status] || STATUS_MAP.pending;
            return (
              <div key={r.id} className="spin-history-row">
                <span className="spin-h-emoji">{r.prize_emoji}</span>
                <div className="spin-h-info">
                  <strong>{r.prize_label}</strong>
                  <span className="spin-h-user">{r.user_name || "—"} • {r.user_email || "—"}</span>
                  <span className="spin-h-date">{fmtDate(r.created_at)}</span>
                  {r.user_preferences && (
                    <div className="spin-h-pref">
                      <span className="spin-h-pref-label">Preferência:</span> {r.user_preferences}
                    </div>
                  )}
                  {(r.survey_won || r.survey_wants) && (
                    <div className="spin-h-pref" style={{ marginTop: 4, background: "rgba(212,175,55,0.1)", borderLeft: "2px solid #D4AF37", paddingLeft: 8 }}>
                      <div style={{ fontSize: "0.75rem", color: "#D4AF37", marginBottom: 2 }}><strong>Pesquisa Pré-Giro:</strong></div>
                      {r.survey_won && <div style={{ marginBottom: 2 }}><span className="spin-h-pref-label">Já ganhou:</span> {r.survey_won}</div>}
                      {r.survey_wants && <div><span className="spin-h-pref-label">Gostaria de:</span> {r.survey_wants}</div>}
                    </div>
                  )}
                </div>
                <span className="spin-h-type">{r.spin_type === "welcome" ? "Boas-vindas" : "Vídeo"}</span>
                <div className="spin-h-actions">
                  <select className={`spin-status-select ${sc.cls}`} value={r.status} onChange={(e) => handleStatusChange(r.id, e.target.value)}>
                    <option value="pending">Pendente</option>
                    <option value="delivered">Entregue</option>
                    <option value="expired">Expirado</option>
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  // ── Survey tab ──
  const renderSurvey = () => (
    <>
      <div className="spin-survey-toolbar">
        <div className="spin-survey-toggle-row">
          <div>
            <strong>Pesquisa pré-giro</strong>
            <p className="spin-survey-toggle-desc">Quando ativada, aparece um popup para a cliente antes de girar perguntando o que ganhou e o que deseja ganhar.</p>
          </div>
          <button
            className={`spin-survey-toggle-btn ${surveyEnabled ? "active" : ""}`}
            onClick={handleToggleSurvey}
            disabled={togglingsurvey}
          >
            {togglingsurvey ? <FontAwesomeIcon icon={faSpinner} spin /> : (surveyEnabled ? "✅ Ativada" : "⭕ Desativada")}
          </button>
        </div>
      </div>

      {surveyLoading ? (
        <div className="spin-loading"><FontAwesomeIcon icon={faSpinner} spin /> Carregando...</div>
      ) : survey.length === 0 ? (
        <div className="spin-empty">
          <FontAwesomeIcon icon={faHistory} style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }} />
          <div>Nenhuma resposta ainda. Ative a pesquisa para começar a coletar!</div>
        </div>
      ) : (
        <>
          <div className="spin-survey-list">
            {survey.map((r) => (
              <div key={r.id} className="spin-survey-row">
                <div className="spin-survey-avatar">{(r.user_name || "?").charAt(0).toUpperCase()}</div>
                <div className="spin-survey-info">
                  <strong className="spin-survey-name">{r.user_name || "—"}</strong>
                  <span className="spin-survey-email">{r.user_email || "—"}</span>
                  <span className="spin-survey-date">{fmtDate(r.created_at)}</span>
                </div>
                <div className="spin-survey-answers">
                  {r.question_won && (
                    <div className="spin-survey-answer">
                      <span className="spin-survey-answer-label">🎀 Ganhou antes:</span>
                      <span className="spin-survey-answer-text">{r.question_won}</span>
                    </div>
                  )}
                  {r.question_wants && (
                    <div className="spin-survey-answer">
                      <span className="spin-survey-answer-label">✨ Quer ganhar:</span>
                      <span className="spin-survey-answer-text">{r.question_wants}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="spin-pagination">
            <button disabled={surveyPage === 0} onClick={() => setSurveyPage((p) => p - 1)}>Anterior</button>
            <span>Página {surveyPage + 1} de {Math.max(1, Math.ceil(surveyTotal / 50))}</span>
            <button disabled={(surveyPage + 1) * 50 >= surveyTotal} onClick={() => setSurveyPage((p) => p + 1)}>Próxima</button>
          </div>
        </>
      )}
    </>
  );

  // ── Tiers tab ──
  const renderTiers = () => (
    <>
      <div className="spin-tier-toolbar">
        <p className="spin-tier-desc">Crie e gerencie as roletas por nível de cliente. Defina os prêmios, pesos e suba o nível das clientes conforme compram mais.</p>
        <button className="spin-grant-btn" onClick={() => { setTierModal("new"); setTierForm({ name: "", emoji: "⭐", color: "#D4AF37", sort_order: tiers.length, is_default: false }); }}>
          <FontAwesomeIcon icon={faPlus} /> Novo Tier
        </button>
      </div>

      {tiersLoading ? (
        <div className="spin-loading"><FontAwesomeIcon icon={faSpinner} spin /> Carregando...</div>
      ) : tiers.length === 0 ? (
        <div className="spin-empty"><FontAwesomeIcon icon={faCog} style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }} /><div>Nenhuma roleta configurada ainda. Crie a primeira clicando em "Novo Tier".</div></div>
      ) : (
        <div className="spin-tiers-list">
          {tiers.map((t) => (
            <div key={t.id} className="spin-tier-card" style={{ borderColor: t.color + "33" }}>
              <div className="spin-tier-header" onClick={() => setExpandedTier(expandedTier === t.id ? null : t.id)}>
                <span className="spin-tier-emoji" style={{ background: t.color + "18" }}>{t.emoji}</span>
                <div className="spin-tier-title">
                  <strong>{t.name}</strong>
                  <span className="spin-tier-meta">
                    {t.prizes_count || 0} {(t.prizes_count || 0) === 1 ? "prêmio" : "prêmios"} configurados
                    {t.is_welcome && <span className="spin-tier-welcome">🎉 BOAS-VINDAS</span>}
                    {t.is_default && !t.is_welcome && <span className="spin-tier-default">PADRÃO</span>}
                  </span>
                  {t.is_welcome && <span className="spin-tier-welcome-desc">Prêmios que toda cliente nova pode ganhar no 1º giro</span>}
                </div>
                <div className="spin-tier-actions-top">
                  <button className="spin-tier-edit" onClick={(e) => { e.stopPropagation(); setTierModal(t); setTierForm({ name: t.name, emoji: t.emoji, color: t.color, sort_order: t.sort_order, is_default: t.is_default }); }}><FontAwesomeIcon icon={faPen} /></button>
                  {!t.is_default && !t.is_welcome && <button className="spin-tier-delete" onClick={(e) => { e.stopPropagation(); handleDeleteTier(t.id); }}><FontAwesomeIcon icon={faTrash} /></button>}
                </div>
                <FontAwesomeIcon icon={expandedTier === t.id ? faArrowUp : faArrowDown} className="spin-tier-chevron" />
              </div>

              {expandedTier === t.id && (
                <div className="spin-tier-prizes">
                  <div className="spin-tier-prizes-header">
                    <span>Prêmios que a cliente pode ganhar nesta roleta</span>
                    <button className="spin-prize-add-btn" onClick={() => { setPrizeModal({ tierId: t.id }); setPrizeForm({ ...EMPTY_PRIZE }); }}>
                      <FontAwesomeIcon icon={faPlus} /> Adicionar
                    </button>
                  </div>
                  {(!t.prizes || t.prizes.length === 0) ? (
                    <div className="spin-tier-no-prizes">Nenhum prêmio ainda. Adicione o primeiro!</div>
                  ) : (
                    <div className="spin-tier-prizes-list">
                      {t.prizes.map((p) => (
                        <div key={p.id} className={`spin-tier-prize-row ${!p.is_active ? "inactive" : ""}`}>
                          <span className="spin-tp-emoji">{p.emoji}</span>
                          <div className="spin-tp-info">
                            <strong>{p.label}</strong>
                            <span className="spin-tp-key">{p.prize_key}</span>
                          </div>
                          <span className="spin-tp-weight">Peso: {p.weight}</span>
                          {!p.is_active && <span className="spin-tp-inactive">OFF</span>}
                          <button className="spin-tp-edit" onClick={() => { setPrizeModal({ tierId: t.id, prize: p }); setPrizeForm({ prize_key: p.prize_key, label: p.label, emoji: p.emoji, weight: p.weight, is_active: p.is_active }); }}><FontAwesomeIcon icon={faPen} /></button>
                          <button className="spin-tp-delete" onClick={() => handleDeletePrize(p.id)}><FontAwesomeIcon icon={faTrash} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );

  return (
    <div className="spin-mgmt">
      <div className="spin-tabs">
        <button className={`spin-tab ${tab === "users" ? "active" : ""}`} onClick={() => setTab("users")}><FontAwesomeIcon icon={faGift} /> Usuárias & Giros</button>
        <button className={`spin-tab ${tab === "history" ? "active" : ""}`} onClick={() => setTab("history")}><FontAwesomeIcon icon={faHistory} /> Prêmios Ganhos</button>
        <button className={`spin-tab ${tab === "tiers" ? "active" : ""}`} onClick={() => setTab("tiers")}><FontAwesomeIcon icon={faCog} /> Tipos de Roleta</button>
        <button className={`spin-tab ${tab === "survey" ? "active" : ""}`} onClick={() => setTab("survey")}><FontAwesomeIcon icon={faStar} /> Pesquisas</button>
      </div>

      {tab === "users" && renderUsers()}
      {tab === "history" && renderHistory()}
      {tab === "tiers" && renderTiers()}
      {tab === "survey" && renderSurvey()}

      {revokeModal && (
        <div className="spin-modal-overlay" onClick={(e) => e.target === e.currentTarget && setRevokeModal(null)}>
          <div className="spin-modal">
            <h3>Remover giro — {revokeModal.name || revokeModal.email}</h3>
            <p className="spin-modal-hint">
              Só remove giros <strong>ainda não usados</strong> (começa pelos mais recentes). Hoje ela tem{" "}
              <strong>{revokeModal.spins_available || 0}</strong> disponíve{(revokeModal.spins_available || 0) === 1 ? "l" : "is"}.
            </p>
            <div className="spin-modal-field">
              <label>Quantidade a remover</label>
              <input
                type="number"
                min={1}
                max={Math.max(1, revokeModal.spins_available || 1)}
                value={revokeSpins}
                onChange={(e) => {
                  const max = Math.max(1, revokeModal.spins_available || 1);
                  setRevokeSpins(Math.min(max, Math.max(1, parseInt(e.target.value, 10) || 1)));
                }}
              />
            </div>
            <div className="spin-modal-field">
              <label>Motivo (opcional)</label>
              <input
                type="text"
                placeholder="Ex.: Concedido por engano"
                value={revokeNote}
                onChange={(e) => setRevokeNote(e.target.value)}
              />
            </div>
            <div className="spin-modal-actions">
              <button type="button" className="spin-modal-cancel" onClick={() => setRevokeModal(null)}>Cancelar</button>
              <button type="button" className="spin-modal-revoke" onClick={handleRevoke} disabled={revoking}>
                {revoking ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faMinus} />}
                {revoking ? " Removendo..." : ` Remover ${revokeSpins} giro${revokeSpins > 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grant spin modal */}
      {grantModal && (
        <div className="spin-modal-overlay" onClick={(e) => e.target === e.currentTarget && setGrantModal(null)}>
          <div className="spin-modal">
            <h3>Dar Giro para {grantModal.name || grantModal.email}</h3>
            <p className="spin-modal-hint">Conceda giros na roleta de presentes. Ex: após a cliente enviar o vídeo de recebimento do pedido.</p>
            
            <div className="spin-modal-row">
              <div className="spin-modal-field">
                <label>Quantidade de giros</label>
                <input type="number" min={1} max={10} value={grantSpins} onChange={(e) => setGrantSpins(Math.max(1, parseInt(e.target.value) || 1))} />
              </div>
              <div className="spin-modal-field">
                <label>Nível da Roleta</label>
                <select value={grantTierId} onChange={(e) => { setGrantTierId(e.target.value); setGrantPrizeKey(""); }}>
                  <option value="">Atual da Usuária ({getTierName(grantModal.wheel_tier_id)})</option>
                  {tiers.map((t) => (
                    <option key={t.id} value={t.id}>{t.emoji} {t.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="spin-modal-field">
              <label>Forçar prêmio específico? (Opcional)</label>
              <select value={grantPrizeKey} onChange={(e) => setGrantPrizeKey(e.target.value)}>
                <option value="">🎲 Sorteio Aleatório (Pesos)</option>
                {grantTierId && tiers.find(t => t.id === grantTierId)?.prizes.map((p) => (
                  <option key={p.prize_key} value={p.prize_key}>{p.emoji} {p.label}</option>
                ))}
                {!grantTierId && <option disabled>Selecione um nível acima para ver os prêmios</option>}
              </select>
            </div>

            <div className="spin-modal-field"><label>Observação (opcional)</label><input type="text" placeholder="Ex: Enviou vídeo de recebimento" value={grantNote} onChange={(e) => setGrantNote(e.target.value)} /></div>
            <div className="spin-modal-actions">
              <button className="spin-modal-cancel" onClick={() => setGrantModal(null)}>Cancelar</button>
              <button className="spin-modal-confirm" onClick={handleGrant} disabled={granting}>
                {granting ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faGift} />}
                {granting ? " Concedendo..." : ` Dar ${grantSpins} Giro${grantSpins > 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tier create/edit modal */}
      {tierModal && (
        <div className="spin-modal-overlay" onClick={(e) => e.target === e.currentTarget && setTierModal(null)}>
          <div className="spin-modal">
            <div className="spin-modal-top"><h3>{tierModal === "new" ? "Criar Novo Nível de Roleta" : `Editar Roleta ${tierModal.emoji} ${tierModal.name}`}</h3><button className="spin-modal-x" onClick={() => setTierModal(null)}><FontAwesomeIcon icon={faXmark} /></button></div>
            <div className="spin-modal-field"><label>Nome do nível</label><input value={tierForm.name} onChange={(e) => setTierForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ex: Rubi, Esmeralda..." /></div>
            <div className="spin-modal-row">
              <div className="spin-modal-field"><label>Emoji</label><input value={tierForm.emoji} onChange={(e) => setTierForm((f) => ({ ...f, emoji: e.target.value }))} /></div>
              <div className="spin-modal-field"><label>Cor</label><input type="color" value={tierForm.color} onChange={(e) => setTierForm((f) => ({ ...f, color: e.target.value }))} /></div>
              <div className="spin-modal-field"><label>Ordem</label><input type="number" value={tierForm.sort_order} onChange={(e) => setTierForm((f) => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} /></div>
            </div>
            <label className="spin-checkbox"><input type="checkbox" checked={tierForm.is_default} onChange={(e) => setTierForm((f) => ({ ...f, is_default: e.target.checked }))} /> Nível padrão para novas clientes</label>
            <div className="spin-modal-actions">
              <button className="spin-modal-cancel" onClick={() => setTierModal(null)}>Cancelar</button>
              <button className="spin-modal-confirm" onClick={handleSaveTier} disabled={!tierForm.name.trim()}>
                <FontAwesomeIcon icon={faCheck} /> Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prize create/edit modal */}
      {prizeModal && (
        <div className="spin-modal-overlay" onClick={(e) => e.target === e.currentTarget && setPrizeModal(null)}>
          <div className="spin-modal">
            <div className="spin-modal-top"><h3>{prizeModal.prize ? "Editar Prêmio" : "Novo Prêmio"}</h3><button className="spin-modal-x" onClick={() => setPrizeModal(null)}><FontAwesomeIcon icon={faXmark} /></button></div>
            <div className="spin-modal-field"><label>Nome do prêmio (aparece na roleta)</label><input value={prizeForm.label} onChange={(e) => setPrizeForm((f) => ({ ...f, label: e.target.value }))} placeholder="Ex: Bolsa Premium, 20% OFF..." /></div>
            <div className="spin-modal-row">
              <div className="spin-modal-field"><label>Identificador único</label><input value={prizeForm.prize_key} onChange={(e) => setPrizeForm((f) => ({ ...f, prize_key: e.target.value }))} placeholder="ex: bolsa_premium" /></div>
              <div className="spin-modal-field"><label>Emoji</label><input value={prizeForm.emoji} onChange={(e) => setPrizeForm((f) => ({ ...f, emoji: e.target.value }))} /></div>
            </div>
            <div className="spin-modal-row">
              <div className="spin-modal-field"><label>Peso (quanto maior, mais chance)</label><input type="number" min={1} value={prizeForm.weight} onChange={(e) => setPrizeForm((f) => ({ ...f, weight: parseInt(e.target.value) || 1 }))} /></div>
              <div className="spin-modal-field" style={{ flex: "0 0 auto" }}>
                <label>Ativo</label>
                <label className="spin-checkbox" style={{ marginTop: 4 }}><input type="checkbox" checked={prizeForm.is_active} onChange={(e) => setPrizeForm((f) => ({ ...f, is_active: e.target.checked }))} /> Sim</label>
              </div>
            </div>
            <div className="spin-modal-actions">
              <button className="spin-modal-cancel" onClick={() => setPrizeModal(null)}>Cancelar</button>
              <button className="spin-modal-confirm" onClick={handleSavePrize} disabled={!prizeForm.label.trim() || !prizeForm.prize_key.trim()}>
                <FontAwesomeIcon icon={faCheck} /> Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User tier change modal */}
      {tierChangeUser && (
        <div className="spin-modal-overlay" onClick={(e) => e.target === e.currentTarget && setTierChangeUser(null)}>
          <div className="spin-modal">
            <h3>Alterar Tier de {tierChangeUser.name || tierChangeUser.email}</h3>
            <p className="spin-modal-hint">Defina o nível da roleta desta cliente. Quanto maior o nível, melhores os prêmios que ela pode ganhar.</p>
            <div className="spin-modal-field">
              <label>Tier</label>
              <select className="spin-tier-select" value={selectedTierId} onChange={(e) => setSelectedTierId(e.target.value)}>
                <option value="">Padrão (default)</option>
                {tiers.map((t) => (
                  <option key={t.id} value={t.id}>{t.emoji} {t.name}</option>
                ))}
              </select>
            </div>
            <div className="spin-modal-actions">
              <button className="spin-modal-cancel" onClick={() => setTierChangeUser(null)}>Cancelar</button>
              <button className="spin-modal-confirm" onClick={handleSetUserTier}>
                <FontAwesomeIcon icon={faCheck} /> Salvar Tier
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpinManagement;
